import { useCallback, useEffect, useMemo } from 'react';

import { objectKeysToCamelCase } from '@mint-lib/api';

import { KeysToSnakeCase } from '@myn/shared/src/utils';

import type { AsyncValidationState, DataFilePreview } from '../api/types.js';
import { useUpdateDataFilePreview } from '../queries/dataFile/preview.js';
import { QueryKeys, useTypedQueryClient } from '../queries/index.js';
import { useDataFileSocket } from './useDataFileSocket.js';
import { useShouldNeverHappen } from './useShouldNeverHappen.js';

export const useAsyncValidation = ({
  dataFileId,
  dataFilePreview,
}: {
  dataFileId: string;
  dataFilePreview: DataFilePreview;
}) => {
  const queryClient = useTypedQueryClient();
  const shouldNeverHappen = useShouldNeverHappen();

  const dataFileSocket = useDataFileSocket(dataFileId);

  const { mutate: updateDataFilePreview } =
    useUpdateDataFilePreview(dataFileId);

  const isSocketConnected = dataFileSocket.connected;

  const validationStateQueryKey = useMemo(
    () => QueryKeys.dataFile.validationState(dataFileId),
    [dataFileId],
  );

  const onValidationProgress = useCallback(
    (response: KeysToSnakeCase<AsyncValidationState>) => {
      const data = objectKeysToCamelCase(response);
      queryClient.setQueryData(validationStateQueryKey, data);
    },
    [queryClient, validationStateQueryKey],
  );

  const onValidationFinished = useCallback(() => {
    queryClient.setQueryData(validationStateQueryKey, null);
    updateDataFilePreview({
      status: 'uncompleted',
    });
  }, [queryClient, updateDataFilePreview, validationStateQueryKey]);

  useEffect(() => {
    if (dataFilePreview.status === 'validating' && !isSocketConnected) {
      shouldNeverHappen(
        'Alarm. Websocket is disconnected during datafile validation',
      );
    }
  }, [dataFilePreview.status, isSocketConnected, shouldNeverHappen]);

  useEffect(() => {
    dataFileSocket.on('validation_progress', onValidationProgress);
    dataFileSocket.on('validation_finished', onValidationFinished);

    return () => {
      dataFileSocket.off('validation_progress');
      dataFileSocket.off('validation_finished');
    };
  }, [dataFileSocket, onValidationProgress, onValidationFinished]);
};
